/**
 * Bootstrap-First Theme System
 *
 * Themes use Bootstrap CSS variable names directly for full ecosystem compatibility.
 */
export interface BootstrapTheme {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly isDark: boolean;
  readonly variables: {
    readonly '--bs-primary': string;
    readonly '--bs-secondary': string;
    readonly '--bs-success': string;
    readonly '--bs-warning': string;
    readonly '--bs-danger': string;
    readonly '--bs-info': string;
    readonly '--bs-light': string;
    readonly '--bs-dark': string;
    readonly '--bs-body-bg': string;
    readonly '--bs-body-color': string;
    readonly '--bs-border-color': string;
    readonly '--bs-primary-rgb': string;
    readonly '--bs-secondary-rgb': string;
    readonly '--bs-secondary-bg': string;
    readonly '--bs-tertiary-bg': string;
    readonly '--bs-secondary-color': string;
    readonly '--bs-border-radius': string;
  };
}

export const bootstrapThemes: readonly BootstrapTheme[] = [
  {
    id: 'light',
    name: 'Clean Light',
    description: 'Bright white with blue accents',
    isDark: false,
    variables: {
      '--bs-primary': '#2563eb',
      '--bs-secondary': '#6b7280',
      '--bs-success': '#16a34a',
      '--bs-warning': '#d97706',
      '--bs-danger': '#dc2626',
      '--bs-info': '#0891b2',
      '--bs-light': '#f3f4f6',
      '--bs-dark': '#1f2937',
      '--bs-body-bg': '#f8fafc',
      '--bs-body-color': '#1e293b',
      '--bs-border-color': '#e2e8f0',
      '--bs-primary-rgb': '37, 99, 235',
      '--bs-secondary-rgb': '107, 114, 128',
      '--bs-secondary-bg': '#ffffff',
      '--bs-tertiary-bg': '#f1f5f9',
      '--bs-secondary-color': '#64748b',
      '--bs-border-radius': '0.375rem',
    },
  },
  {
    id: 'dark',
    name: 'Dark',
    description: 'Dark theme with blue accents',
    isDark: true,
    variables: {
      '--bs-primary': '#3b82f6',
      '--bs-secondary': '#6b7280',
      '--bs-success': '#22c55e',
      '--bs-warning': '#f59e0b',
      '--bs-danger': '#ef4444',
      '--bs-info': '#06b6d4',
      '--bs-light': '#374151',
      '--bs-dark': '#111827',
      '--bs-body-bg': '#0f172a',
      '--bs-body-color': '#e2e8f0',
      '--bs-border-color': '#334155',
      '--bs-primary-rgb': '59, 130, 246',
      '--bs-secondary-rgb': '107, 114, 128',
      '--bs-secondary-bg': '#1e293b',
      '--bs-tertiary-bg': '#334155',
      '--bs-secondary-color': '#94a3b8',
      '--bs-border-radius': '0.375rem',
    },
  },
  {
    id: 'nature',
    name: 'Nature',
    description: 'Fresh greens and earth tones',
    isDark: false,
    variables: {
      '--bs-primary': '#16a34a',
      '--bs-secondary': '#6b7280',
      '--bs-success': '#22c55e',
      '--bs-warning': '#ea580c',
      '--bs-danger': '#dc2626',
      '--bs-info': '#0d9488',
      '--bs-light': '#f0fdf4',
      '--bs-dark': '#14532d',
      '--bs-body-bg': '#f8fafc',
      '--bs-body-color': '#1e293b',
      '--bs-border-color': '#d1fae5',
      '--bs-primary-rgb': '22, 163, 74',
      '--bs-secondary-rgb': '107, 114, 128',
      '--bs-secondary-bg': '#ffffff',
      '--bs-tertiary-bg': '#ecfdf5',
      '--bs-secondary-color': '#64748b',
      '--bs-border-radius': '0.5rem',
    },
  },
  {
    id: 'ocean',
    name: 'Ocean',
    description: 'Cool blues and teals',
    isDark: false,
    variables: {
      '--bs-primary': '#0891b2',
      '--bs-secondary': '#6b7280',
      '--bs-success': '#16a34a',
      '--bs-warning': '#d97706',
      '--bs-danger': '#dc2626',
      '--bs-info': '#0284c7',
      '--bs-light': '#ecfeff',
      '--bs-dark': '#164e63',
      '--bs-body-bg': '#f8fafc',
      '--bs-body-color': '#1e293b',
      '--bs-border-color': '#cffafe',
      '--bs-primary-rgb': '8, 145, 178',
      '--bs-secondary-rgb': '107, 114, 128',
      '--bs-secondary-bg': '#ffffff',
      '--bs-tertiary-bg': '#ecfeff',
      '--bs-secondary-color': '#64748b',
      '--bs-border-radius': '0.375rem',
    },
  },
  {
    id: 'sunset',
    name: 'Sunset',
    description: 'Warm oranges and reds',
    isDark: false,
    variables: {
      '--bs-primary': '#ea580c',
      '--bs-secondary': '#78716c',
      '--bs-success': '#16a34a',
      '--bs-warning': '#f59e0b',
      '--bs-danger': '#dc2626',
      '--bs-info': '#f97316',
      '--bs-light': '#fff7ed',
      '--bs-dark': '#7c2d12',
      '--bs-body-bg': '#fffbf5',
      '--bs-body-color': '#292524',
      '--bs-border-color': '#fed7aa',
      '--bs-primary-rgb': '234, 88, 12',
      '--bs-secondary-rgb': '120, 113, 108',
      '--bs-secondary-bg': '#ffffff',
      '--bs-tertiary-bg': '#fff7ed',
      '--bs-secondary-color': '#78716c',
      '--bs-border-radius': '0.5rem',
    },
  },
  {
    id: 'lavender',
    name: 'Lavender',
    description: 'Soft purples and violets',
    isDark: false,
    variables: {
      '--bs-primary': '#7c3aed',
      '--bs-secondary': '#6b7280',
      '--bs-success': '#16a34a',
      '--bs-warning': '#d97706',
      '--bs-danger': '#dc2626',
      '--bs-info': '#8b5cf6',
      '--bs-light': '#f5f3ff',
      '--bs-dark': '#4c1d95',
      '--bs-body-bg': '#faf5ff',
      '--bs-body-color': '#1e293b',
      '--bs-border-color': '#e9d5ff',
      '--bs-primary-rgb': '124, 58, 237',
      '--bs-secondary-rgb': '107, 114, 128',
      '--bs-secondary-bg': '#ffffff',
      '--bs-tertiary-bg': '#f5f3ff',
      '--bs-secondary-color': '#64748b',
      '--bs-border-radius': '0.5rem',
    },
  },
  {
    id: 'rose',
    name: 'Rose',
    description: 'Elegant pinks and corals',
    isDark: false,
    variables: {
      '--bs-primary': '#e11d48',
      '--bs-secondary': '#6b7280',
      '--bs-success': '#16a34a',
      '--bs-warning': '#d97706',
      '--bs-danger': '#dc2626',
      '--bs-info': '#f43f5e',
      '--bs-light': '#fff1f2',
      '--bs-dark': '#881337',
      '--bs-body-bg': '#fff5f6',
      '--bs-body-color': '#1e293b',
      '--bs-border-color': '#fecdd3',
      '--bs-primary-rgb': '225, 29, 72',
      '--bs-secondary-rgb': '107, 114, 128',
      '--bs-secondary-bg': '#ffffff',
      '--bs-tertiary-bg': '#fff1f2',
      '--bs-secondary-color': '#64748b',
      '--bs-border-radius': '0.5rem',
    },
  },
  {
    id: 'midnight',
    name: 'Midnight',
    description: 'Deep indigo dark theme',
    isDark: true,
    variables: {
      '--bs-primary': '#818cf8',
      '--bs-secondary': '#6b7280',
      '--bs-success': '#4ade80',
      '--bs-warning': '#fbbf24',
      '--bs-danger': '#f87171',
      '--bs-info': '#a78bfa',
      '--bs-light': '#312e81',
      '--bs-dark': '#0f0e1a',
      '--bs-body-bg': '#1e1b4b',
      '--bs-body-color': '#e0e7ff',
      '--bs-border-color': '#3730a3',
      '--bs-primary-rgb': '129, 140, 248',
      '--bs-secondary-rgb': '107, 114, 128',
      '--bs-secondary-bg': '#312e81',
      '--bs-tertiary-bg': '#3730a3',
      '--bs-secondary-color': '#a5b4fc',
      '--bs-border-radius': '0.375rem',
    },
  },
];
